import { useCallback, useEffect, useState } from "react";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { H2 } from "../Headers/Headings";
import { InfoItem } from "../Survey/InfoItem";
import { useApiData } from "../../contexts/ApiData";
import Button, { LinkButton } from "../Button/Button";
import { Tip } from "../Tip/Index";
import Card from "../Card/Card";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import PricingPlans from "../PricingPlans/PricingPlans";
import styles from "./SubscriptionDetails.module.css";

type Subscription = {
  id: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  cancellationDate: string | null;
};

export const SubscriptionDetails = () => {
  const { get, post } = useApiData();
  const t = useTranslations("subscription");
  const d = useDateFormatter();

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await get("/subscriptions/user");
    setSubscriptions(result ?? []);
    setLoaded(true);
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const openStripePortal = async (subscriptionId: string) => {
    const result = await post(`/payment/create-portal-session?subscriptionId=${subscriptionId}`);
    if (result?.gotoUrl) window.location.href = result.gotoUrl;
  };

  const cancelSubscription = async (subscriptionId: string) => {
    if (!window.confirm(t("cancelConfirm"))) return;
    const result = await post("/payment/cancel-subscription", { subscriptionId });
    if (result?.success) {
      setCancelSuccess(t("cancelSuccess"));
      await load();
    }
  };

  if (loaded && subscriptions.length === 0) {
    return <PricingPlans />;
  }

  return (
    <div className={styles.wrapper}>
      <H2>{t("detailsHeading")}</H2>
      <Tip>{t("stripeInfo")}</Tip>
      {cancelSuccess && <InfoMessage type='success'>{cancelSuccess}</InfoMessage>}
      {subscriptions.map((sub) => {
        const isCancelled = !!sub.cancellationDate;
        const endLabel = isCancelled ? t("endDateLabel") : t("renewalDateLabel");
        return (
          <Card key={sub.id} className={styles.card} padding='large'>
            <InfoItem label={t("subscriptionStatus")} value={t(`status_${sub.status}`)} />
            <InfoItem label={t("subscriptionType")} value={t(`type_${sub.type}`)} />
            <InfoItem label={t("startDateLabel")} value={d(sub.startDate)} />
            <InfoItem label={endLabel} value={d(sub.endDate)} />
            <div className={styles.actions}>
              <Button onClick={() => openStripePortal(sub.id)}>{t("openPortal")}</Button>
              {!isCancelled && <LinkButton onClick={() => cancelSubscription(sub.id)}>{t("cancelSubscription")}</LinkButton>}
            </div>
          </Card>
        );
      })}
    </div>
  );
};
